/** @internal — only for use within useHomePageOrchestrator */
import type { snapshot } from 'valtio';
import { formVersionsConfig } from '../../../docList/configs/formVersionsConfig';
import { cleanFilterValues } from '../../../docList/cleanFilterValues';
import type { ColumnSpec } from '../../../docList/DocList.types';
import type { DocListTableStore } from '../../../docList/createDocListTableStore';
import { homePageConfig } from '#types';
import type {
  FormVersionWithPath,
  ListFormVersionsDocsRequest,
} from '#types';

export function buildDefaultVisibility(): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  for (const col of formVersionsConfig.columns) {
    if (
      col.hiddenByDefault === true ||
      col.id.startsWith('field_') ||
      col.id.startsWith('entity_') ||
      col.id.startsWith('group_')
    ) {
      visibility[col.id] = false;
    }
  }
  return visibility;
}

export function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function buildServerWhereFromStore(
  storeSnap: ReturnType<typeof snapshot<DocListTableStore>>,
  columns: ColumnSpec<FormVersionWithPath>[],
): NonNullable<ListFormVersionsDocsRequest['where']> {
  return storeSnap.columnFilters.flatMap((filter) => {
    const col = columns.find((c) => c.id === filter.id);
    if (!col?.serverFilterField) return [];
    const value = cleanFilterValues(filter.value);
    if (value.length === 0) return [];
    return [{ fieldPath: col.serverFilterField, op: col.serverFilterOp ?? 'in', value }];
  });
}

export function buildServerOrderByFromStore(
  storeSnap: ReturnType<typeof snapshot<DocListTableStore>>,
  columns: ColumnSpec<FormVersionWithPath>[],
): Array<{ fieldPath: string; direction: 'asc' | 'desc' }> | undefined {
  if (storeSnap.sorting.length === 0) return undefined;

  const sortEntry = storeSnap.sorting[0];
  const col = columns.find((c) => c.id === sortEntry.id);
  const fieldPath = col?.serverSortField;
  if (!fieldPath) return undefined;

  return [{
    fieldPath,
    direction: sortEntry.desc ? 'desc' : 'asc',
  }];
}

export function hasExtraServerFilters(
  storeSnap: ReturnType<typeof snapshot<DocListTableStore>>,
  columns: ColumnSpec<FormVersionWithPath>[],
  reportId: string,
): boolean {
  const report = homePageConfig.formsTable.reports.find((r) => r.id === reportId);
  const reportFilterIds = new Set(report?.initialFilters?.map((f) => f.id) ?? []);

  return storeSnap.columnFilters.some((filter) => {
    if (reportFilterIds.has(filter.id)) return false;
    const col = columns.find((c) => c.id === filter.id);
    if (!col?.serverFilterField) return false;
    return cleanFilterValues(filter.value).length > 0;
  });
}

export function areReportFiltersPresent(
  storeSnap: ReturnType<typeof snapshot<DocListTableStore>>,
  reportId: string,
): boolean {
  const report = homePageConfig.formsTable.reports.find((r) => r.id === reportId);
  if (!report?.initialFilters || report.initialFilters.length === 0) return true;

  const activeFilterIds = new Set(storeSnap.columnFilters.map((f) => f.id));
  return report.initialFilters.every((filter) => activeFilterIds.has(filter.id));
}

export function resolveInitialFilters(reportId: string, userId: string | undefined): Array<{ id: string; value: unknown }> {
  const report = homePageConfig.formsTable.reports.find((r) => r.id === reportId);
  if (!report?.initialFilters) return [];

  return report.initialFilters.flatMap((filter) => {
    const resolvedValues = filter.value
      .map((value) => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object' && 'type' in value) {
          if ((value as { type?: unknown }).type === 'currentUserId') {
            return userId ?? '';
          }
        }
        return '';
      })
      .filter((v) => v !== '');

    if (resolvedValues.length === 0) return [];
    return [{ id: filter.id, value: resolvedValues }];
  });
}
